/**
 * @nanokart/tryon-studio-sdk
 * Event type definitions - Versioned as public API
 */

import { SDK_VERSION, EVENT_SCHEMA_VERSION, StudioMode, ResolvedFeatures } from './config';

// ============================================================================
// Base Event (all events must extend this)
// ============================================================================

export interface BaseEvent {
  /** SDK version that emitted this event */
  sdkVersion: string;
  /** Event schema version (for backward compat) */
  schemaVersion: string;
  /** Event name */
  eventName: string;
  /** Session ID for correlation */
  sessionId?: string;
  /** Unique correlation ID for this event */
  correlationId: string;
  /** ISO timestamp */
  timestamp: string;
}

// ============================================================================
// Event Factory
// ============================================================================

export function createBaseEvent(
  eventName: string,
  sessionId?: string | null
): BaseEvent {
  return {
    sdkVersion: SDK_VERSION,
    schemaVersion: EVENT_SCHEMA_VERSION,
    eventName,
    sessionId: sessionId ?? undefined,
    correlationId: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
  };
}

// ============================================================================
// Specific Event Types
// ============================================================================

export interface ReadyEvent extends BaseEvent {
  eventName: 'ready';
  payload: {
    partnerId: string;
    mode: StudioMode;
    features: ResolvedFeatures;
  };
}

export interface TryOnCompleteEvent extends BaseEvent {
  eventName: 'tryon_complete';
  payload: {
    productId: string;
    productName: string;
    resultImageUrl: string;
    inputPhotoUrl: string;
    garmentImageUrl: string;
    cacheKey: string;
    processingTimeMs: number;
  };
}

export interface MomentGeneratedEvent extends BaseEvent {
  eventName: 'moment_generated';
  payload: {
    momentType: string;
    resultImageUrl: string;
    tryonCacheKey: string;
    processingTimeMs: number;
  };
}

export interface AddToCartEvent extends BaseEvent {
  eventName: 'add_to_cart';
  payload: {
    productId: string;
    productName: string;
    variantId?: string;
    size?: string;
    color?: string;
    price: number;
    currency: string;
    quantity: number;
    tryonImageUrl?: string;
  };
}

export interface RemoveFromCartEvent extends BaseEvent {
  eventName: 'remove_from_cart';
  payload: {
    productId: string;
    variantId?: string;
  };
}

export interface SessionStartEvent extends BaseEvent {
  eventName: 'session_start';
  payload: {
    customerId?: string;
    phone?: string;
    mode: StudioMode;
    features: ResolvedFeatures;
  };
}

export interface SessionEndEvent extends BaseEvent {
  eventName: 'session_end';
  payload: {
    duration: number; // seconds
    tryonsGenerated: number;
    momentsGenerated: number;
    itemsInCart: number;
    endReason: 'user_action' | 'limit_reached' | 'timeout' | 'error';
  };
}

export interface ErrorEvent extends BaseEvent {
  eventName: 'error';
  payload: {
    code: string;
    message: string;
    recoverable: boolean;
    context?: Record<string, unknown>;
  };
}

export interface AnalyticsEvent extends BaseEvent {
  eventName: 'analytics';
  payload: {
    name: string;
    properties: Record<string, unknown>;
  };
}

// ============================================================================
// Event Callbacks Interface
// ============================================================================

export interface TryOnStudioEvents {
  onReady?: (event: ReadyEvent) => void;
  onTryOnComplete?: (event: TryOnCompleteEvent) => void;
  onMomentGenerated?: (event: MomentGeneratedEvent) => void;
  onAddToCart?: (event: AddToCartEvent) => void;
  onRemoveFromCart?: (event: RemoveFromCartEvent) => void;
  onSessionStart?: (event: SessionStartEvent) => void;
  onSessionEnd?: (event: SessionEndEvent) => void;
  onError?: (event: ErrorEvent) => void;
  onAnalyticsEvent?: (event: AnalyticsEvent) => void;
}

// ============================================================================
// Error Codes
// ============================================================================

export const ERROR_CODES = {
  // Auth errors
  AUTH_INVALID_KEY: 'auth/invalid-key',
  AUTH_EXPIRED_SESSION: 'auth/expired-session',
  AUTH_RATE_LIMITED: 'auth/rate-limited',
  AUTH_DOMAIN_NOT_ALLOWED: 'auth/domain-not-allowed',

  // Credit errors
  CREDITS_INSUFFICIENT: 'credits/insufficient',
  CREDITS_LIMIT_REACHED: 'credits/limit-reached',

  // Image errors
  IMAGE_TOO_LARGE: 'image/too-large',
  IMAGE_INVALID_FORMAT: 'image/invalid-format',
  IMAGE_UPLOAD_FAILED: 'image/upload-failed',
  IMAGE_NO_PERSON_DETECTED: 'image/no-person-detected',

  // API errors
  API_TRYON_FAILED: 'api/tryon-failed',
  API_MOMENTS_FAILED: 'api/moments-failed',
  API_NETWORK_ERROR: 'api/network-error',
  API_SERVER_ERROR: 'api/server-error',
  API_TIMEOUT: 'api/timeout',

  // Session errors
  SESSION_EXPIRED: 'session/expired',
  SESSION_INVALID: 'session/invalid',
  SESSION_NOT_FOUND: 'session/not-found',

  // Config errors
  CONFIG_INVALID: 'config/invalid',
  CONFIG_MISSING_REQUIRED: 'config/missing-required',

  // General
  UNKNOWN_ERROR: 'unknown/error',
} as const;

export type ErrorCode = typeof ERROR_CODES[keyof typeof ERROR_CODES];
